import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, CheckCircle, MessageSquare, Wrench, DollarSign,
  X, Loader, Clock, AlertCircle, Building, Phone, Mail, Calendar
} from 'lucide-react';
import api from '../../api/apiClient';

interface TimelineEvent {
  eventType: string;
  description: string;
  createdAt: string;
}

interface Contractor {
  id: number;
  name: string;
  email: string;
  phone?: string;
  specialty?: string;
}

interface MaintenanceDetail {
  id: number;
  category: string;
  urgency: string;
  priority: number;
  status: string;
  description: string;
  propertyAddress?: string;
  propertyId?: number;
  renterName?: string;
  renterEmail?: string;
  renterPhone?: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerId?: number;
  createdAt: string;
  slaDue?: string;
  slaExpired?: boolean;
  complianceRequired?: boolean;
  assignedContractorId?: number;
  assignedContractorName?: string;
  quoteAmount?: number;
  quoteStatus?: string;
  ownerApprovalStatus?: string;
  ownerApprovalDate?: string;
  scheduledDate?: string;
  timeline: TimelineEvent[];
}

export default function AgentMaintenanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [request, setRequest] = useState<MaintenanceDetail | null>(null);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);

  // Action modals
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showOwnerApproval, setShowOwnerApproval] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  // Form data
  const [infoQuestion, setInfoQuestion] = useState('');
  const [selectedContractorId, setSelectedContractorId] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('10:00');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (id) {
      loadRequest();
      loadContractors();
    }
  }, [id]);

  const loadRequest = async () => {
    try {
      const response = await api.get<MaintenanceDetail>(`/agent/maintenance/requests/${id}`);
      setRequest(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  const loadContractors = async () => {
    try {
      const response = await api.get<any>('/contractors');
      setContractors(response || []);
    } catch (err) {
      // Fallback if no contractors endpoint
      setContractors([]);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoQuestion.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/request-info`, { question: infoQuestion });
      setSuccess('Information request sent to tenant');
      setShowRequestInfo(false);
      setInfoQuestion('');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to send request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedContractorId) return;
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/assign`, { contractorId: Number(selectedContractorId) });
      setSuccess('Contractor assigned successfully');
      setShowAssign(false);
      setSelectedContractorId('');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to assign contractor');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestOwnerApproval = async () => {
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/request-owner-approval`, { message: approvalMessage });
      setSuccess('Approval request sent to owner');
      setShowOwnerApproval(false);
      setApprovalMessage('');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to request approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveQuote = async () => {
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/quote/approve`, {});
      setSuccess('Quote approved successfully');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to approve quote');
    } finally {
      setActionLoading(false);
    }
  };


  const handleScheduleVisit = async () => {
    if (!scheduledDate) return;
    setActionLoading(true);
    setError('');
    try {
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      await api.post(`/agent/maintenance/requests/${id}/schedule`, { scheduledAt });
      setSuccess('Visit scheduled successfully');
      setShowSchedule(false);
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule visit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/complete`, { notes: completionNotes });
      setSuccess('Marked as completed');
      setShowComplete(false);
      setCompletionNotes('');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to complete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/agent/maintenance/requests/${id}/reject`, { reason: rejectReason });
      setSuccess('Request rejected');
      setShowReject(false);
      setRejectReason('');
      loadRequest();
    } catch (err: any) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OPEN': return 'bg-blue-100 text-blue-800';
      case 'ASSIGNED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'PENDING_APPROVAL': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toUpperCase()) {
      case 'EMERGENCY': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Request not found</p>
        <button onClick={() => navigate('/agent/maintenance')} className="mt-4 text-secondary hover:text-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/agent/maintenance')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Request #{request.id}</h1>
          <p className="text-gray-600">{request.category}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status & Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(request.status)}`}>
                {request.status?.replace(/_/g, ' ')}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getUrgencyColor(request.urgency)}`}>
                {request.urgency}
              </span>
              {request.complianceRequired && (
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">Compliance Required</span>
              )}
              {request.slaExpired && (
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">⚠️ SLA Expired</span>
              )}
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
              <p className="text-gray-900">{request.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Property:</span>
                <p className="font-medium text-gray-900">{request.propertyAddress}</p>
              </div>
              <div>
                <span className="text-gray-500">Priority Score:</span>
                <p className="font-medium text-gray-900">{request.priority}</p>
              </div>
              <div>
                <span className="text-gray-500">Submitted:</span>
                <p className="font-medium text-gray-900">{formatDate(request.createdAt)}</p>
              </div>
              {request.slaDue && (
                <div>
                  <span className="text-gray-500">SLA Due:</span>
                  <p className={`font-medium ${request.slaExpired ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatDate(request.slaDue)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quote Section */}
          {request.quoteAmount && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Contractor Quote</h2>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-2xl font-bold text-gray-900">${request.quoteAmount.toFixed(2)}</p>
                  <p className="text-sm text-gray-500">Quote Status: {request.quoteStatus}</p>
                </div>
                {request.quoteStatus === 'SUBMITTED' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowOwnerApproval(true)}
                      className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
                    >
                      Request Owner Approval
                    </button>
                    <button
                      onClick={handleApproveQuote}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setShowReject(true)}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
              {request.ownerApprovalStatus && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Owner Approval:</strong> {request.ownerApprovalStatus}
                    {request.ownerApprovalDate && ` (${formatDate(request.ownerApprovalDate)})`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <button
                onClick={() => setShowRequestInfo(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-secondary text-secondary rounded-lg hover:bg-secondary/5"
              >
                <MessageSquare className="w-4 h-4" />
                Request Info
              </button>

              <button
                onClick={() => setShowAssign(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-primary text-primary rounded-lg hover:bg-primary/5"
              >
                <Wrench className="w-4 h-4" />
                Assign Contractor
              </button>

              <button
                onClick={() => setShowSchedule(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-primary text-primary rounded-lg hover:bg-primary/5"
              >
                <Calendar className="w-4 h-4" />
                Schedule Visit
              </button>

              {request.status !== 'COMPLETED' && (
                <button
                  onClick={() => setShowComplete(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Complete
                </button>
              )}

              <button
                onClick={() => setShowReject(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                <X className="w-4 h-4" />
                Reject Request
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h2>
            {request.timeline && request.timeline.length > 0 ? (
              <div className="space-y-4">
                {request.timeline.map((event, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2"></div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{event.eventType.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-gray-600">{event.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(event.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No activity yet</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tenant Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
            </div>
            <div className="space-y-3">
              <p className="font-medium text-gray-900">{request.renterName || 'Unknown'}</p>
              {request.renterEmail && (
                <a href={`mailto:${request.renterEmail}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                  <Mail className="w-4 h-4" /> {request.renterEmail}
                </a>
              )}
              {request.renterPhone && (
                <a href={`tel:${request.renterPhone}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                  <Phone className="w-4 h-4" /> {request.renterPhone}
                </a>
              )}
            </div>
          </div>

          {/* Owner Info */}
          {request.ownerName && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Property Owner</h2>
              </div>
              <div className="space-y-3">
                <p className="font-medium text-gray-900">{request.ownerName}</p>
                {request.ownerEmail && (
                  <a href={`mailto:${request.ownerEmail}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                    <Mail className="w-4 h-4" /> {request.ownerEmail}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Assigned Contractor */}
          {request.assignedContractorId && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Assigned Contractor</h2>
              </div>
              <p className="font-medium text-gray-900">{request.assignedContractorName || `Contractor #${request.assignedContractorId}`}</p>
            </div>
          )}

          {/* Scheduled Visit */}
          {request.scheduledDate && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Scheduled Visit</h2>
              </div>
              <p className="font-medium text-gray-900">{formatDate(request.scheduledDate)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showRequestInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Request More Information</h3>
            <textarea
              value={infoQuestion}
              onChange={(e) => setInfoQuestion(e.target.value)}
              placeholder="What additional information do you need from the tenant?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 resize-none"
              rows={4}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowRequestInfo(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleRequestInfo} disabled={actionLoading || !infoQuestion.trim()} className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Assign Contractor</h3>
            {contractors.length > 0 ? (
              <select
                value={selectedContractorId}
                onChange={(e) => setSelectedContractorId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              >
                <option value="">Select a contractor...</option>
                {contractors.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.specialty ? `(${c.specialty})` : ''}</option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                value={selectedContractorId}
                onChange={(e) => setSelectedContractorId(e.target.value)}
                placeholder="Enter Contractor ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              />
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowAssign(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleAssign} disabled={actionLoading || !selectedContractorId} className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule Contractor Visit</h3>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={today} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <select value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'].map(t => (
                    <option key={t} value={t}>{t.replace(':00', ':00 ' + (parseInt(t) < 12 ? 'AM' : 'PM'))}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowSchedule(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleScheduleVisit} disabled={actionLoading || !scheduledDate} className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOwnerApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Request Owner Approval</h3>
            <p className="text-sm text-gray-600 mb-4">Send this quote to the property owner for approval before proceeding.</p>
            <textarea
              value={approvalMessage}
              onChange={(e) => setApprovalMessage(e.target.value)}
              placeholder="Additional message for the owner (optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 resize-none"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowOwnerApproval(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleRequestOwnerApproval} disabled={actionLoading} className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Send to Owner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showComplete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Mark as Completed</h3>
            <textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Completion notes (optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 resize-none"
              rows={4}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowComplete(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleComplete} disabled={actionLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Request</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejection"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 resize-none"
              rows={4}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowReject(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleRejectRequest} disabled={actionLoading || !rejectReason.trim()} className="flex-1 px-4 py-2 bg-accent text-white rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



